<template>
	<div v-if="isUserAllowedInLevel2 && isAuthenticatedUserInGroupAdmin">
		<div>
			<BaseHeader :title="$t('fee_table').toString()" />
			<div v-if="!isLoading">
				<div class="p-2 flex mt-4 px-8">
					<div class="grid gap-4 flex-grow md:grid-cols-2">
						<div v-if="isLevel2Admin" class="text-left">
							{{ $t('damage_amount_net') }}
							<div class="italic">{{ $t('place_values_for_order_form') }}</div>
						</div>
						<div class="text-left" v-if="isLevel2Admin">
							{{ $t('fee_net') }}
							<div class="italic">{{ $t('place_values_for_order_form') }}</div>
						</div>
					</div>
				</div>

				<div class="px-8 space-x-4 flex flex-grow flex-row">
					<div class="flex flex-col flex-grow w-1/2" v-if="isLevel2Admin">
						<div v-for="(element, index) in companyData.fees" :key="index" class="grid grid-cols-2 gap-4">
							<BaseInputNumber
								:ref="`BaseInput.amount_of_damage_${index}`"
								:placeholder="$t('damage_amount')"
								v-model="companyData.fees[index].amount_of_damage"
								:precision="2"
								:tab-index="index + 100"
								:disabled="false"
								:required="true"
							/>
							<BaseInputNumber
								:ref="`BaseInput.companyFees_${index}`"
								:placeholder="$t('fee')"
								v-model="companyData.fees[index].fee"
								:precision="2"
								:tab-index="index + 1000"
								:required="true"
							/>
						</div>
					</div>
				</div>
				<div v-if="isLevel2Admin" class="px-8 flex tr grid gap-4 gap-2 flex-grow md:grid-cols-2">
					<BaseInputNumber
						:ref="`BaseInput.amount_of_damage_max_fee`"
						:placeholder="$t('per_2.5_euro')"
						:precision="2"
						:tab-index="100"
						:disabled="true"
					/>
					<BaseInputNumber
						:ref="`BaseInput.companyFees_maxFee`"
						:placeholder="$t('fee')"
						v-model="damageAssessorCompany.fee_max_damage_amount"
						:precision="2"
						:tab-index="1000"
						:required="true"
					/>
				</div>
				<div>
					<BaseHeader :title="$t('extra_costs').toString()" />
					<div class="main">
						<div v-if="isLevel2Admin">
							<div class="text-left mb-4">
								{{ $t('eur_values_as_net') }}
							</div>
							<div class="grid md:grid-cols-2 gap-4">
								<BaseInputNumber
									v-model="damageAssessorCompany.cost_per_photo"
									ref="BaseInputNumber.cost_per_photo"
									:label="$t('cost_per_photo')"
									:sublabel="$t('first_photo_set')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.cost_per_photo_duplicate"
									ref="BaseInputNumber.cost_per_photo_duplicate"
									:label="$t('cost_per_photo')"
									:sublabel="$t('per_second_copy')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.writing_cost_per_page"
									ref="BaseInputNumber.writing_cost_per_page"
									:label="$t('writing_costs_per_page')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.writing_cost_per_page_duplicate"
									ref="BaseInputNumber.writing_cost_per_page_duplicate"
									:label="$t('writing_costs_per_page')"
									:sublabel="$t('per_second_copy')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.postage_phone"
									ref="BaseInputNumber.postage_phone"
									:label="$t('postage_phone')"
									:sublabel="$t('lump_sum')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.travel_costs_per_km"
									ref="BaseInputNumber.travel_costs_per_km"
									:label="$t('driving_costs_per_km')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.maximum_billed_km"
									ref="BaseInputNumber.maximum_billed_km"
									:label="$t('max_billed_km')"
									:precision="0"
									:value-range="{ min: 0, max: 99 }"
									:currency="{ prefix: '', suffix: ' km' }"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.cost_error_memory_readout"
									ref="BaseInputNumber.cost_error_memory_readout"
									:label="$t('read_error_log')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.residual_value_exchange"
									ref="BaseInputNumber.residual_value_exchange"
									:label="$t('salvage_exchange')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.cost_provision_of_original_image_files"
									ref="BaseInputNumber.cost_provision_of_original_image_files"
									:label="$t('provision_original_image_data')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.layer_thickness_measurement"
									ref="BaseInputNumber.layer_thickness_measurement"
									:label="$t('layer_thickness_measurement')"
									:precision="2"
									:required="true"
								/>
								<BaseInputNumber
									v-model="damageAssessorCompany.cost_fetch_calculation_software"
									ref="BaseInputNumber.cost_fetch_calculation_software"
									:label="$t('cost_fetch_calculation_software')"
									:precision="2"
									:required="true"
								/>
							</div>
						</div>
					</div>
				</div>
				<Footer
					class="justify-between m-8 content-center justify-items-center"
					style="display: flex; justify-content: center;"
					:is-back-button="false"
				>
					<template #continueButton>
						<SaveButton style="min-width: fit-content; max-height: fit-content; " @click="save" class="btn-blue ml-6">
							{{ $t('general.save') }}
						</SaveButton>
					</template>
				</Footer>
			</div>
			<LoadingSpinnerWidget v-else />
		</div>
	</div>
	<Forbidden v-else />
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import { CompanyApiFactory, DamageassessorpricingApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber';
import BaseButton from '@/components/forms/buttons/BaseButton';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import BaseInput from '@/components/forms/inputs/BaseInput';
import Authentication from '@/mixins/Authentication';
import Validate from '@/mixins/Validate';
import Footer from '@/components/damagecases/Footer.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import { default as LoadingSpinnerWidget } from '@/components/navigation/LoadingSpinner.vue';
import Forbidden from '@/views/default/Forbidden.vue';
import { makeShadowCopy } from '@/plugins/Utils';
import CompanyRepository from '@/api/repositories/CompanyRepository';

export default {
	name: 'EditPDFPricing',
	components: { Forbidden, LoadingSpinnerWidget, SaveButton, BaseInputNumber, BaseHeader, Footer },
	mixins: [LoadingSpinner, Authentication, Validate],
	data() {
		return {
			companyData: null,
			isLoading: true,
			damageAssessorCompany: {},
		};
	},
	mounted() {
		if (this.isUserAllowedInLevel2 && this.isAuthenticatedUserInGroupAdmin) {
			this.isLoading = true;
			this.damageAssessorCompany = makeShadowCopy(this.getUserCompany);

			this.getDamageAssessorPricing();
			this.getExtraCosts();
		}
	},
	computed: {
		getTitle() {
			return this.$t('fee_agreement');
		},
	},
	methods: {
		getDamageAssessorPricing() {
			this.setLoadingStatus(true);
			return DamageassessorpricingApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damageassessorpricingGet(undefined, 100)
				.then(response => {
					this.companyData = response.data.companyData;

					this.isLoading = false;
					this.setLoadingStatus(false);
				})
				.catch(error => {
					this.isLoading = false;
					this.setLoadingStatus(false);
				});
		},
		getExtraCosts() {
			new CompanyRepository()
				.getDamageAssessorCompanyExtraCosts()
				.then(response => {
					this.damageAssessorCompany = response.data;
				})
				.catch(e => {
					console.log(e);
				});
		},
		save() {
			if (!this.validate()) {
				this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
				return;
			}
			this.setLoadingStatus(true);
			const saveDamageAssessorCompany = new CompanyRepository().saveDamageAssessorCompanyExtraCosts(this.damageAssessorCompany);

			const saveDamageAssessorPricing = DamageassessorpricingApiFactory(
				apiDefaults.config(),
				apiDefaults.basePath,
				apiDefaults.axios,
			).damageassessorpricingPut(this.companyData.fees);

			Promise.all([saveDamageAssessorCompany, saveDamageAssessorPricing])
				.then(() => {
					this.$store.commit('setConfirmLeaving', false);
					this.setLoadingStatus(false);
					this.$store.dispatch('updateAuthenticatedUser');
					this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
				})
				.catch(error => {
					this.setLoadingStatus(false);
				});
		},
	},
};
</script>
